import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { requestOtp, resetPassword } from "../services/authService";
// Using lucide-react (standard for modern React apps)
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';

export const ForgotPasswordLayer = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const [data, setData] = useState({
    email: "",
    otp: "",
    new_password: ""
  });

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await requestOtp(data.email);
      if (res.status === "Success") {
        toast.success("OTP sent to your email!");
        setStep(2);
      } else {
        toast.error(res.message || "Failed to send OTP");
      }
    } catch (err) {
      toast.error("User not found or connection error.");
    } finally {
      setLoading(false);
    }
  };

  const handleFinalReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await resetPassword(data);
      if (res.status === "Success") {
        toast.success("Password updated successfully!");
        navigate("/sign-in");
      } else {
        toast.error(res.message || "Invalid OTP or request.");
      }
    } catch (err) {
      toast.error("Reset failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <style>
        {`
          .auth-container {
            display: flex;
            min-height: 100vh;
            align-items: center;
            justify-content: center;
            background-color: #f4f7fe;
            padding: 20px;
            font-family: 'Inter', sans-serif;
          }
          .auth-card {
            display: flex;
            width: 100%;
            max-width: 950px;
            background: #fff;
            border-radius: 24px;
            overflow: hidden;
            box-shadow: 0 20px 40px rgba(0,0,0,0.06);
          }
          .auth-left {
            flex: 1;
            background: #fff;
            padding: 60px;
            display: flex;
            flex-direction: column;
            justify-content: center;
            border-right: 1px solid #f0f0f0;
          }
          .auth-right {
            flex: 1.2;
            padding: 60px;
            background: #fff;
          }
          .form-group { margin-bottom: 24px; position: relative; }
          .form-label { display: block; margin-bottom: 8px; font-weight: 600; color: #333; font-size: 14px; }
          .input-field {
            width: 100%;
            padding: 14px 18px;
            border-radius: 12px;
            border: 1px solid #e2e8f0;
            background: #f8fafc;
            font-size: 15px;
            transition: all 0.3s ease;
            box-sizing: border-box;
          }
          .input-field:focus {
            outline: none;
            border-color: #6236ff;
            background: #fff;
            box-shadow: 0 0 0 4px rgba(98, 54, 255, 0.1);
          }
          .password-toggle {
            position: absolute;
            right: 14px;
            top: 60px;
            background: none;
            border: none;
            cursor: pointer;
            color: #94a3b8;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .submit-btn {
            width: 100%;
            padding: 16px;
            border-radius: 12px;
            border: none;
            background: #6236ff;
            color: #fff;
            font-size: 16px;
            font-weight: 600;
            cursor: pointer;
            transition: 0.3s;
            margin-top: 10px;
          }
          .submit-btn:hover { background: #4e29d1; transform: translateY(-1px); }
          .submit-btn:disabled { background: #94a3b8; cursor: not-allowed; }

          /* Responsive Breakpoints */
          @media (max-width: 992px) {
            .auth-left { display: none; } /* Hidden on Tablets and Mobile */
            .auth-card { max-width: 500px; justify-content: center; }
            .auth-right { flex: 1; padding: 40px 30px; }
          }
          
          @media (max-width: 480px) {
            .auth-container { padding: 10px; }
            .auth-card { border-radius: 16px; }
            .auth-right h5 { font-size: 24px !important; }
          }
        `}
      </style>

      <div className="auth-card">
        {/* Left Branding Side (Hidden on Mobile/Tablet via CSS) */}
        <div className="auth-left">
          <h5 style={{ fontSize: '42px', fontWeight: '800', marginBottom: '20px', color: '#1e293b' }}>
            Forgot <br /> Password
          </h5>
          <p style={{ fontSize: '17px', color: '#64748b', lineHeight: '1.6' }}>
            Don't worry, it happens. Reset your credentials to get back to your dashboard.
          </p>
        </div>

        {/* Right Form Side */}
        <div className="auth-right">
          <h5 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '8px', color: '#1e293b' }}>
            {step === 1 ? "Account Recovery" : "Setup New Password"}
          </h5>
          <p style={{ color: '#94a3b8', marginBottom: '32px', fontSize: '14px' }}>
            {step === 1 ? "Enter your email to receive a secure OTP code" : `We sent a code to ${data.email}`}
          </p>

          <form onSubmit={step === 1 ? handleRequestOtp : handleFinalReset}>
            {step === 1 ? (
              <div className="form-group">
                <label className="form-label">Email Address</label>
                <input 
                  type="email" 
                  className="input-field"
                  required
                  placeholder="name@school.com"
                  value={data.email}
                  onChange={(e) => setData({ ...data, email: e.target.value })}
                />
              </div>
            ) : (
              <>
                <div className="form-group">
                  <label className="form-label">OTP Code</label>
                  <input 
                    type="text" 
                    className="input-field"
                    required
                    maxLength="6"
                    placeholder="Enter 6-digit code"
                    value={data.otp}
                    onChange={(e) => setData({ ...data, otp: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">New Password</label>
                  <input 
                    type={showPassword ? "text" : "password"} 
                    className="input-field"
                    required
                    placeholder="Create strong password"
                    value={data.new_password}
                    onChange={(e) => setData({ ...data, new_password: e.target.value })}
                  />
                  <button 
                    type="button" 
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </>
            )}

            <button type="submit" className="submit-btn text-center" disabled={loading}>
              {loading ? "Please wait..." : (step === 1 ? "Send Reset Code" : "Update Password")}
            </button>
          </form>

          <div style={{ marginTop: '25px', textAlign: 'center' }}>
            <Link to="/sign-in" style={{ 
              color: '#6236ff', 
              textDecoration: 'none', 
              fontWeight: '600', 
              fontSize: '14px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <ArrowLeft size={16} /> Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};